import { useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Sheet,
    YStack,
} from 'smoothie';

import {
    formatCardBrand,
    type PaymentMethodSummary,
    useRemovePaymentMethod,
} from '../hooks/usePaymentMethod';

interface PaymentMethodDeleteSheetProps {
    isOpen: boolean;
    onClose: () => void;
    paymentMethod: PaymentMethodSummary | null;
    onDeleted: () => void;
}

const PaymentMethodDeleteSheet = ({
    isOpen,
    onClose,
    paymentMethod,
    onDeleted,
}: PaymentMethodDeleteSheetProps) => {
    const { mutateAsync: removePaymentMethod, isPending: isDeleting } =
        useRemovePaymentMethod();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Delete payment method"
            showCloseButton
        >
            <BodyMediumRegular textAlign="center" color="$gray1000">
                {paymentMethod?.brand != null
                    ? `${formatCardBrand(paymentMethod.brand)} *${paymentMethod.last4} will be deleted from your account.`
                    : 'This payment method will be deleted from your account.'}
            </BodyMediumRegular>

            {errorMessage != null && (
                <BodyMediumRegular color="$red1100" textAlign="center">
                    {errorMessage}
                </BodyMediumRegular>
            )}

            <YStack marginTop={12} gap={8}>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    tone="danger"
                    size={ButtonSize.LARGE}
                    text={isDeleting ? 'Deleting…' : 'Delete'}
                    disabled={isDeleting}
                    onPress={async () => {
                        setErrorMessage(null);
                        try {
                            await removePaymentMethod({ body: {} });
                            onDeleted();
                        } catch (err) {
                            setErrorMessage(
                                err instanceof Error
                                    ? err.message
                                    : 'Something went wrong. Please try again.',
                            );
                        }
                    }}
                />
                <Button
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.LARGE}
                    text="Cancel"
                    disabled={isDeleting}
                    onPress={onClose}
                />
            </YStack>
        </Sheet>
    );
};

export default PaymentMethodDeleteSheet;
